/**
 * Capacity & Utilisation screen (task 14.2, R6, R7.1, R7.6, R7.7).
 *
 * The primary Manpower analysis screen: Demand_Hours, Capacity_Hours and
 * Utilization across the planning year's periods, as the KPI row, the
 * Bottleneck_State-coloured utilisation strip and the reconciliation table of UI
 * spec §5.1.
 *
 * ## Scope, including the cross-scope total
 *
 * Capacity is held per FF role and Region, so the natural unit of this screen is
 * one (role, Region) scope — but the question a planner opens it with is usually
 * "is the field force as a whole able to deliver next year". Both are available:
 * the role and Region filters each offer an "all" option, and the endpoint
 * aggregates server-side when one is omitted.
 *
 * The aggregation deliberately does not happen here. Summing per-scope responses
 * in the client would put `classifyBottleneck`'s threshold comparison in a second
 * place, and R6.6's "changing the threshold reclassifies on the next read" is only
 * true while there is exactly one classifier. See `capacity-utilisation-data.ts`.
 *
 * ## Every figure is drill-throughable
 *
 * R6.5 requires the excess to be traceable to what caused it. Each period carries
 * its contributing Initiatives, ranked, so both the strip and the reconciliation
 * table open the same panel naming them — and the table shows the largest
 * contributor inline, since "what is driving April" is usually the whole question.
 *
 * Capability: `manpower:read` to view (R7.1, R7.6, R7.7). The gate renders a
 * no-access notice; it does not short-circuit before the hooks, so the hook order
 * is identical on every render while the session query resolves.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { InfoIcon } from 'lucide-react';
import { usePageChrome } from '@/components/layout/page-chrome';
import { hasCapability, useSession } from '@/app/session/session';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CapacityStateBadge } from '@/components/ui/capacity-state-badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FilterActions, FilterBar, FilterSelect } from '@/shared/components/filter-bar';
import { cn } from '@/lib/utils';
import { useManpowerReferenceSet } from '../manpower-reference-data';
import {
  ALL_REGIONS,
  ALL_ROLES,
  useCapacityUtilisation,
  type FfRoleScope,
  type PeriodInitiative,
  type RegionScope,
} from './capacity-utilisation-data';
import {
  deriveCapacityUtilisationKpis,
  deriveReconciliationRows,
  deriveUtilisationMatrix,
  getUtilisationBarColor,
  type CapacityUtilisationKpis,
  type PeriodLabel,
  type ReconciliationRow,
  type UtilisationMatrix,
  type UtilisationMatrixCell,
} from './capacity-utilisation-model';
import { FormulaTransparencyBlock } from '../shared/FormulaTransparencyBlock';
import { periodText } from '../shared/period-label';
import { RoleBadge } from '../shared/RoleBadge';
import { currentPlanningYear, formatHours, planningYearOptions } from '../shared/manpower-format';
import { FF_ROLE_OPTIONS, ffRoleLabel } from '../ff-role-options';

/** The four Bottleneck_States, in the order the legend lists them (R6.4). */
const LEGEND_STATES = ['normal', 'warning', 'over_capacity', 'data_incomplete'] as const;

/** The period whose contributing Initiatives the drill-through panel is showing. */
interface DrillThrough {
  readonly periodLabel: PeriodLabel;
  readonly demandHours: number;
  readonly initiatives: readonly PeriodInitiative[];
}

export function CapacityUtilisationPage(): React.JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  const canRead = hasCapability(session.data, 'manpower:read');

  usePageChrome({
    title: t('manpower.capacityUtilisation.title'),
    subtitle: t('manpower.capacityUtilisation.subtitle'),
  });

  const [planningYear, setPlanningYear] = useState<number>(() => currentPlanningYear());
  // Both default to the aggregate: the screen opens on "the whole field force next
  // year", which is the question it exists to answer, and narrowing to one scope is
  // the reader's next step rather than a prerequisite. This is also why neither
  // filter needs to wait for the Region list to load.
  const [ffRole, setFfRole] = useState<FfRoleScope>(ALL_ROLES);
  const [regionId, setRegionId] = useState<RegionScope>(ALL_REGIONS);
  const [drillThrough, setDrillThrough] = useState<DrillThrough | null>(null);

  const regionsQuery = useManpowerReferenceSet('region', { enabled: canRead });
  const regions = useMemo(() => regionsQuery.data ?? [], [regionsQuery.data]);
  const regionName = useMemo(
    () => regions.find((region) => region.id === regionId)?.name,
    [regions, regionId],
  );

  const utilisationQuery = useCapacityUtilisation(
    { planningYear, ffRole, regionId },
    { enabled: canRead },
  );

  const response = utilisationQuery.data;
  const periods = useMemo(() => response?.periods ?? [], [response]);
  const kpis = useMemo(
    () => (response === undefined ? null : deriveCapacityUtilisationKpis(response)),
    [response],
  );
  const matrix = useMemo(() => deriveUtilisationMatrix(periods), [periods]);
  const reconciliationRows = useMemo(() => deriveReconciliationRows(periods), [periods]);

  if (session.isSuccess && !canRead) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="text-muted-foreground text-[12.3px]">
          {t('manpower.capacityUtilisation.noAccess')}
        </p>
      </div>
    );
  }

  const hasNoRegions = regionsQuery.isSuccess && regions.length === 0;
  const openDrillThrough = (cell: UtilisationMatrixCell | ReconciliationRow): void => {
    setDrillThrough({
      periodLabel: cell.periodLabel,
      demandHours: cell.demandHours,
      initiatives: cell.contributingInitiatives,
    });
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-4">
        <FilterBar label={t('manpower.capacityUtilisation.filterBarLabel')}>
          <FilterSelect
            label={t('manpower.capacityUtilisation.planningYear')}
            value={String(planningYear)}
            onChange={(value) => setPlanningYear(Number(value))}
          >
            {planningYearOptions().map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label={t('manpower.capacityUtilisation.filterRole')}
            value={ffRole}
            onChange={(value) => setFfRole(value as FfRoleScope)}
          >
            <option value={ALL_ROLES}>{t('manpower.capacityUtilisation.allRoles')}</option>
            {FF_ROLE_OPTIONS.map((role) => (
              <option key={role.id} value={role.id}>
                {role.label}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label={t('manpower.capacityUtilisation.filterRegion')}
            value={regionId}
            onChange={(value) => setRegionId(value as RegionScope)}
          >
            <option value={ALL_REGIONS}>{t('manpower.capacityUtilisation.allRegions')}</option>
            {regions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </FilterSelect>

          <FilterActions>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setPlanningYear(currentPlanningYear());
                setFfRole(ALL_ROLES);
                setRegionId(ALL_REGIONS);
              }}
            >
              {t('common.reset')}
            </Button>
          </FilterActions>
        </FilterBar>

        {hasNoRegions && (
          <Alert>
            <AlertTitle>{t('manpower.capacityUtilisation.noRegionsTitle')}</AlertTitle>
            <AlertDescription>{t('manpower.capacityUtilisation.noRegionsBody')}</AlertDescription>
          </Alert>
        )}

        {kpis !== null && periods.length > 0 && (
          <KpiRow
            kpis={kpis}
            scopeLabel={
              regionId === ALL_REGIONS
                ? t('manpower.capacityUtilisation.scopeAllRegions', { count: kpis.regionCount })
                : (regionName ?? t('manpower.capacityUtilisation.scopeOneRegion'))
            }
          />
        )}

        {/* The durable, visible consequence of capacity that cannot cover its
            planned demand — including capacity a writer deliberately reduced on the
            Capacity Inputs screen, where the same condition is a warning
            acknowledged once at the moment of the change (R6.4, R6.5). */}
        {kpis !== null && kpis.overCapacityCount > 0 && (
          <Alert className="border-[var(--risk)]">
            <AlertTitle>{t('manpower.capacityUtilisation.overCapacityTitle')}</AlertTitle>
            <AlertDescription>
              {t('manpower.capacityUtilisation.overCapacityBody', {
                count: kpis.overCapacityCount,
                hours: formatHours(kpis.totalExcessHours),
              })}
            </AlertDescription>
          </Alert>
        )}

        {utilisationQuery.isError && (
          <Alert variant="destructive">
            <AlertTitle>{t('common.error')}</AlertTitle>
            <AlertDescription>{t('manpower.capacityUtilisation.loadError')}</AlertDescription>
          </Alert>
        )}

        {matrix.rows.length > 0 && (
          <Card className="gap-1 p-5">
            <p className="text-txt text-[13px] font-semibold">
              {t('manpower.capacityUtilisation.matrixHeading', {
                roles: matrix.rows.map((row) => ffRoleLabel(row.ffRole)).join(' & '),
              })}
            </p>
            <p className="text-muted-foreground mb-3 text-[11.5px]">
              {t('manpower.capacityUtilisation.matrixSubtitle', {
                threshold: kpis?.capacityThreshold ?? 0,
              })}
            </p>
            <UtilisationMatrixGrid matrix={matrix} onSelect={openDrillThrough} />
            <Legend threshold={kpis?.capacityThreshold ?? 0} />
          </Card>
        )}

        {/* The reconciliation table of §5.1, and the screen's loading and empty
            states, in one card — the same chain the Initiatives list uses, so a
            first load and an empty scope read as this card having nothing to show
            rather than as separate framed surfaces appended to the page. */}
        {!utilisationQuery.isError && !hasNoRegions && (
          <section aria-labelledby="cu-table-heading" className="flex flex-col gap-2">
            <SectionHeading id="cu-table-heading">
              {t('manpower.capacityUtilisation.reconciliationHeading')}
            </SectionHeading>
            <Card className="gap-4 p-5">
              {utilisationQuery.isPending ? (
                <p className="text-muted-foreground text-[12.3px]">{t('common.loading')}</p>
              ) : reconciliationRows.length === 0 ? (
                <p className="text-muted-foreground text-[12.3px]">
                  {t('manpower.capacityUtilisation.noData')}
                </p>
              ) : (
                <ReconciliationTable rows={reconciliationRows} onSelect={openDrillThrough} />
              )}
            </Card>
          </section>
        )}

        <FormulaTransparencyBlock
          allocationMethod={t('manpower.capacityUtilisation.allocationMethod')}
        />

        <DrillThroughDialog
          drillThrough={drillThrough}
          onClose={() => {
            setDrillThrough(null);
          }}
        />
      </div>
    </TooltipProvider>
  );
}

/** A section's 10.5px uppercase micro-heading, above the card it names (UI spec §3.1). */
function SectionHeading({
  id,
  children,
}: {
  readonly id: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <h2 id={id} className="text-label text-[10.5px] font-semibold tracking-wide uppercase">
      {children}
    </h2>
  );
}

/** The four headline figures, each with the line that explains what it is made of. */
function KpiRow({
  kpis,
  scopeLabel,
}: {
  readonly kpis: CapacityUtilisationKpis;
  readonly scopeLabel: string;
}): React.JSX.Element {
  const { t } = useTranslation();

  // The role split, as "ADE 62% · BDE 38%". A single-role scope reads as the role's
  // own name: "ADE 100%" states a share of something with only one part in it.
  const roleSummary =
    kpis.roleSplit.length === 0
      ? t('manpower.capacityUtilisation.noRoleDemand')
      : kpis.roleSplit.length === 1
        ? ffRoleLabel(kpis.roleSplit[0]?.ffRole ?? '')
        : kpis.roleSplit
            .map((share) =>
              t('manpower.capacityUtilisation.roleShare', {
                role: ffRoleLabel(share.ffRole),
                percent: share.sharePercent.toFixed(0),
              }),
            )
            .join(' · ');

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        label={t('manpower.capacityUtilisation.totalDemand')}
        value={t('manpower.hours', { value: formatHours(kpis.totalDemandHours) })}
        subline={`${t('manpower.capacityUtilisation.kpiInitiatives', {
          count: kpis.contributingInitiativeCount,
        })} · ${roleSummary}`}
        info={t('manpower.capacityUtilisation.infoDemand')}
      />
      <KpiCard
        label={t('manpower.capacityUtilisation.totalCapacity')}
        value={t('manpower.hours', { value: formatHours(kpis.totalCapacityHours) })}
        subline={
          kpis.capacityAssumptions === null
            ? t('manpower.capacityUtilisation.kpiAssumptionsMixed')
            : t('manpower.capacityUtilisation.kpiAssumptions', {
                hours: kpis.capacityAssumptions.productiveHoursPerWorkday.toFixed(2),
                availability: kpis.capacityAssumptions.availabilityFactor.toFixed(2),
              })
        }
        info={t('manpower.capacityUtilisation.infoCapacity')}
      />
      <KpiCard
        label={t('manpower.capacityUtilisation.utilisationKpi')}
        value={
          kpis.overallUtilisation === null
            ? t('manpower.absent')
            : t('manpower.percent', { value: kpis.overallUtilisation.toFixed(1) })
        }
        subline={`${scopeLabel} · ${t('manpower.capacityUtilisation.kpiThreshold', {
          threshold: kpis.capacityThreshold,
        })}`}
        info={t('manpower.capacityUtilisation.infoUtilisation')}
      />
      <KpiCard
        label={t('manpower.capacityUtilisation.bottlenecks')}
        value={String(kpis.periodsInBreach)}
        subline={t('manpower.capacityUtilisation.kpiExcessHours', {
          hours: formatHours(kpis.totalExcessHours),
        })}
        info={t('manpower.capacityUtilisation.infoBreach')}
        {...(kpis.periodsInBreach > 0 && { tone: 'risk' as const })}
      />
    </div>
  );
}

/**
 * One KPI: a micro-label, the figure, and the line that says what it is made of.
 *
 * `tone` is `'risk'` only for a figure that reports a problem, and is left off
 * otherwise: colour here means "this number needs attention", so applying it
 * unconditionally to a card that can legitimately read zero would spend the signal
 * on nothing.
 *
 * The info affordance is a real `button`, not a `title` attribute on the label:
 * `title` is unreachable by keyboard and unread by most screen readers, and this
 * text explains how a governed number was derived rather than decorating it.
 */
function KpiCard({
  label,
  value,
  subline,
  info,
  tone,
}: {
  readonly label: string;
  readonly value: string;
  readonly subline: string;
  readonly info: string;
  readonly tone?: 'risk';
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    // UI spec §3.2's anatomy: a 10.5px uppercase micro-label above a 24px/600
    // tabular value, with the supporting line beneath.
    <Card className="gap-1 p-5">
      <div className="flex items-start justify-between gap-2">
        <span className="text-label text-[10.5px] font-semibold tracking-wide uppercase">
          {label}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={t('manpower.capacityUtilisation.infoAria', { label })}
              className="text-muted-foreground hover:text-txt focus-visible:outline-accent-line shrink-0 rounded-[var(--radius-control)] focus-visible:outline-2 focus-visible:outline-offset-1"
            >
              <InfoIcon className="size-3.5" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent variant="panel" className="max-w-[260px]">
            <p className="text-[11.5px] leading-[1.5]">{info}</p>
          </TooltipContent>
        </Tooltip>
      </div>
      <span
        className={cn(
          'text-[24px] font-semibold tabular-nums',
          tone === 'risk' ? 'text-[var(--risk)]' : 'text-txt',
        )}
      >
        {value}
      </span>
      <span className="text-muted-foreground text-[11px]">{subline}</span>
    </Card>
  );
}

/**
 * The utilisation matrix: one row per FF role, one column per period, each cell a
 * proportional bar coloured by Bottleneck_State (R6.2, R6.4, R7.1, UI spec §5.1).
 *
 * ## A row per role, not one aggregate row
 *
 * Capacity is held per FF role, so a role is the unit that corresponds to a real,
 * separately staffed pool. A period totalling 70 percent can hide ADE at 130 and BDE
 * at 20, and the aggregate row said the month was fine. The matrix makes the
 * offending pool the thing you see.
 *
 * ## A table, because it is one
 *
 * Two header axes and a value at each intersection is what a `table` element means,
 * and it is the only structure that lets a screen reader announce "ADE, April, 130
 * percent" when reading a cell. Each cell is a `button` so the drill-through R6.5
 * requires is reachable by keyboard, and the percentage is printed in every cell so
 * the state never rests on colour alone (R10.6, R18.8).
 */
function UtilisationMatrixGrid({
  matrix,
  onSelect,
}: {
  readonly matrix: UtilisationMatrix;
  readonly onSelect: (cell: UtilisationMatrixCell) => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-x-[3px] border-spacing-y-[3px]">
        <caption className="sr-only">
          {t('manpower.capacityUtilisation.matrixCaption')}
        </caption>
        <thead>
          <tr>
            <th scope="col" className="sr-only">
              {t('manpower.capacityUtilisation.filterRole')}
            </th>
            {matrix.periodLabels.map((column) => (
              <th
                key={column.periodId}
                scope="col"
                className="text-label text-[10.5px] font-semibold tracking-wide uppercase"
              >
                <PeriodName label={column.label} variant="compact" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.rows.map((row) => (
            <tr key={row.ffRole}>
              <th scope="row" className="pr-1 text-left">
                <RoleBadge role={row.ffRole} />
              </th>
              {row.cells.map((cell) => (
                <td key={cell.periodId} className="align-middle">
                  {cell.present ? (
                    <button
                      type="button"
                      onClick={() => {
                        onSelect(cell);
                      }}
                      className="focus-visible:outline-accent-line bg-line-soft relative block h-[26px] w-full min-w-[46px] overflow-hidden rounded-[2px] focus-visible:outline-2 focus-visible:outline-offset-1"
                      title={t('manpower.capacityUtilisation.cellTooltip', {
                        period: periodText(t, cell.periodLabel, 'full'),
                        demand: formatHours(cell.demandHours),
                        count: cell.contributingInitiatives.length,
                      })}
                    >
                      {/* The track is the role's capacity for the period; the bar is
                          its load against it, clamped — past 100% the colour and the
                          printed figure carry the overload, because a bar cannot
                          overrun the capacity it is drawn against. */}
                      <span
                        aria-hidden
                        className="absolute inset-y-0 left-0 block rounded-[2px]"
                        style={{
                          width: `${String(cell.barPercent)}%`,
                          backgroundColor: getUtilisationBarColor(cell.bottleneckState),
                        }}
                      />
                      {/* Centred over the track rather than inside the bar: at 9% the
                          bar is a sliver with no room for text, and a label that
                          jumps in and out of its own bar is unreadable down a row. */}
                      <span className="text-txt relative flex h-full items-center justify-center text-[11px] font-semibold tabular-nums">
                        {cell.utilisation === null
                          ? t('manpower.absent')
                          : t('manpower.percent', { value: cell.utilisation.toFixed(0) })}
                      </span>
                    </button>
                  ) : (
                    // No Capacity_Period for this role in this period: an empty cell,
                    // not `0%`. See `UtilisationMatrixCell.present`.
                    <span
                      className="border-line block h-[26px] w-full min-w-[46px] rounded-[2px] border border-dashed"
                      title={t('manpower.capacityUtilisation.noPoolForPeriod')}
                    />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The Bottleneck_State key, with the thresholds the states are defined by (R6.4).
 *
 * The threshold is interpolated rather than written into the copy: it is configurable
 * (R6.6), so a legend reading "85–100%" beside figures classified against 90 would be
 * a confident lie about how the colours were chosen.
 */
function Legend({ threshold }: { readonly threshold: number }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
      {LEGEND_STATES.map((state) => (
        <span key={state} className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="block size-2.5 shrink-0 rounded-[2px]"
            style={{ backgroundColor: getUtilisationBarColor(state) }}
          />
          <span className="text-muted-foreground text-[11px]">
            {t(`manpower.capacityUtilisation.legend.${state}`, { threshold })}
          </span>
        </span>
      ))}
    </div>
  );
}

/** The reconciliation table of R6.5:/** The reconciliation table of R6.5: the numbers per period, their state and what drives them. */
function ReconciliationTable({
  rows,
  onSelect,
}: {
  readonly rows: readonly ReconciliationRow[];
  readonly onSelect: (row: ReconciliationRow) => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Table>
      <caption className="sr-only">{t('manpower.capacityUtilisation.tableCaption')}</caption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col" className="text-[10.5px] uppercase">
            {t('manpower.capacityUtilisation.period')}
          </TableHead>
          <TableHead scope="col" className="text-right text-[10.5px] uppercase">
            {t('manpower.capacityUtilisation.demand')}
          </TableHead>
          <TableHead scope="col" className="text-right text-[10.5px] uppercase">
            {t('manpower.capacityUtilisation.capacity')}
          </TableHead>
          <TableHead scope="col" className="text-right text-[10.5px] uppercase">
            {t('manpower.capacityUtilisation.utilization')}
          </TableHead>
          <TableHead scope="col" className="text-right text-[10.5px] uppercase">
            {t('manpower.capacityUtilisation.excessOrFree')}
          </TableHead>
          <TableHead scope="col" className="text-[10.5px] uppercase">
            {t('manpower.capacityUtilisation.status')}
          </TableHead>
          <TableHead scope="col" className="text-right text-[10.5px] uppercase">
            {t('manpower.capacityUtilisation.topDriver')}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.periodId}>
            {/* `tabular-nums` on a date column too, not only on the figures: it is
                what makes the day numbers in a column of `17 Sep – 31 Dec 2026`
                line up instead of drifting with each glyph's own width. */}
            <TableCell className="text-[12.3px] font-medium tabular-nums">
              <PeriodName label={row.periodLabel} />
            </TableCell>
            <TableCell className="text-right text-[12.3px] tabular-nums">
              {t('manpower.hours', { value: formatHours(row.demandHours) })}
            </TableCell>
            <TableCell className="text-right text-[12.3px] tabular-nums">
              {row.capacityHours === null
                ? t('manpower.absent')
                : t('manpower.hours', { value: formatHours(row.capacityHours) })}
              {/* R3.4: a partial total says so, rather than passing as complete. */}
              {row.dataIncompleteScopeCount > 0 && (
                <span className="text-muted-foreground ml-1 text-[11px]">
                  {t('manpower.capacityUtilisation.partialCapacity', {
                    count: row.dataIncompleteScopeCount,
                  })}
                </span>
              )}
            </TableCell>
            <TableCell className="text-right text-[12.3px] font-medium tabular-nums">
              {row.utilisation === null
                ? t('manpower.absent')
                : t('manpower.percent', { value: row.utilisation.toFixed(1) })}
            </TableCell>
            <TableCell className="text-right text-[12.3px] tabular-nums">
              <ExcessCell direction={row.excessDirection} hours={row.excessOrRemainingHours} />
            </TableCell>
            <TableCell>
              <CapacityStateBadge state={row.bottleneckState} />
            </TableCell>
            <TableCell className="text-right text-[12.3px]">
              {row.topInitiative === null ? (
                <span className="text-muted-foreground">{t('manpower.absent')}</span>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    onSelect(row);
                  }}
                  className="focus-visible:outline-accent-line hover:text-accent-hover max-w-[220px] truncate rounded-[var(--radius-control)] text-right focus-visible:outline-2 focus-visible:outline-offset-1"
                  title={t('manpower.capacityUtilisation.topDriverTitle', {
                    ref: row.topInitiative.initiativeRef,
                    hours: formatHours(row.topInitiative.demandHours),
                    count: row.contributingInitiatives.length,
                  })}
                >
                  {row.topInitiative.title}
                </button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * The drill-through: the Initiatives loading one period, ranked (R6.5).
 *
 * Each row links to the Initiative's own Detail_Panel, so the trail from "April is
 * over capacity" to "this is the initiative to move" is a single click. Rendered
 * from the period data already in hand rather than fetched on open — the calculation
 * response carries each period's contributors, so there is nothing to wait for and
 * no second request that could disagree with the row that opened it.
 */
function DrillThroughDialog({
  drillThrough,
  onClose,
}: {
  readonly drillThrough: DrillThrough | null;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Dialog
      open={drillThrough !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle>
            {drillThrough === null ? (
              t('manpower.capacityUtilisation.drillThrough.title')
            ) : (
              <PeriodName label={drillThrough.periodLabel} />
            )}
          </DialogTitle>
          <DialogDescription>
            {drillThrough === null
              ? ''
              : t('manpower.capacityUtilisation.drillThrough.description', {
                  count: drillThrough.initiatives.length,
                  hours: formatHours(drillThrough.demandHours),
                })}
          </DialogDescription>
        </DialogHeader>

        {drillThrough !== null && drillThrough.initiatives.length === 0 ? (
          <p className="text-muted-foreground text-[12.3px]">
            {t('manpower.capacityUtilisation.drillThrough.empty')}
          </p>
        ) : (
          <Table>
            <caption className="sr-only">
              {t('manpower.capacityUtilisation.drillThrough.tableCaption')}
            </caption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className="text-[10.5px] uppercase">
                  {t('manpower.capacityUtilisation.drillThrough.colInitiative')}
                </TableHead>
                <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                  {t('manpower.capacityUtilisation.drillThrough.colDrivers')}
                </TableHead>
                <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                  {t('manpower.capacityUtilisation.drillThrough.colHours')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(drillThrough?.initiatives ?? []).map((initiative) => (
                <TableRow key={initiative.initiativeId}>
                  <TableCell className="text-[12.3px]">
                    <Link
                      to={`/initiatives/${initiative.initiativeId}`}
                      onClick={onClose}
                      className="focus-visible:outline-accent-line hover:text-accent-hover rounded-[var(--radius-control)] focus-visible:outline-2 focus-visible:outline-offset-1"
                    >
                      <span className="font-mono text-[11.5px]">{initiative.initiativeRef}</span>
                      <span className="ml-2">{initiative.title}</span>
                    </Link>
                  </TableCell>
                  <TableCell className="text-right text-[12.3px] tabular-nums">
                    {initiative.driverCount}
                  </TableCell>
                  <TableCell className="text-right text-[12.3px] font-medium tabular-nums">
                    {t('manpower.hours', { value: formatHours(initiative.demandHours) })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * A period's name, resolved from the catalogue (R18.9).
 *
 * A period is identified by its effective date range rather than by a calendar
 * identifier, so the name is derived from that range — see `periodLabelOf` in the
 * view model. Month names reuse the existing `month.short` block so they read the
 * same as everywhere else in the product.
 *
 * ## Two lengths, one definition
 *
 * `compact` is for the utilisation strip, whose columns are a twelfth of the card
 * wide: it drops the year, because the planning year is a filter on the same screen
 * and repeating it twelve times spends the only horizontal room the column has.
 * `full` is for the table, the drill-through and anything a reader might read out of
 * context, so it always carries the year.
 *
 * Both come from the same {@link PeriodLabel}, which is why they cannot drift into
 * two different ideas of how a period is written. Dates are never rendered as raw
 * `yyyy-mm-dd` here: `2026-09-17 – 2026-12-31` is three lines of digits in a narrow
 * column and reads as data rather than as a date.
 */
/** {@link periodText} as an element, for the common case of rendering it inline. */
function PeriodName({
  label,
  variant = 'full',
}: {
  readonly label: PeriodLabel;
  readonly variant?: 'compact' | 'full';
}): React.JSX.Element {
  const { t } = useTranslation();
  return <>{periodText(t, label, variant)}</>;
}

/**
 * The excess/free cell, worded from the catalogue (R18.9).
 *
 * The direction comes from the view model; only the wording is resolved here.
 */
function ExcessCell({
  direction,
  hours,
}: {
  readonly direction: ReconciliationRow['excessDirection'];
  readonly hours: number | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const value = formatHours(Math.abs(hours ?? 0));

  switch (direction) {
    case 'over':
      return (
        <span className="text-[var(--risk)]">
          {t('manpower.capacityUtilisation.excessOver', { value })}
        </span>
      );
    case 'free':
      return <>{t('manpower.capacityUtilisation.excessFree', { value })}</>;
    case 'exact':
      return <>{t('manpower.capacityUtilisation.excessExact')}</>;
    case 'unknown':
      return <>{t('manpower.absent')}</>;
  }
}
